import { planVaultImport, VAULTED_IMPORT_FIELDS, type ImportRow } from './importRouting';
import { isPseudonymWord, isIssuedWord, letterOf, type Pseudonym } from '../sanitise/pseudonyms';
import type { VaultRecordPlain } from './vaultSession';

/**
 * Converting a class that is already running — real names, real marks, thirty students — into a
 * protected one.
 *
 * ── WHY THIS IS A DIFFERENT PROBLEM FROM THE MIGRATION NEXT DOOR ────────────────────────────────
 *
 * `migrateLegacyPii` moves plaintext out of a class that is ALREADY pseudonymous: it reads
 * `realFirstName`/`realLastName` from the owner-only roster and says, in its own words, "the class
 * document keeps its pseudonym untouched". That is correct for the class it was written for and
 * completely wrong for this one. An ordinary class has no pseudonym to keep: the child's actual name is
 * the `firstName` on the class document, which is the array every classmate can read.
 *
 * Run that planner against a live class and `email`, `studentNumber`, `preferredName` and `pronouns`
 * move to the vault while `firstName` and `lastName` stay exactly where they are — and the banner then
 * says "Protected. We store an encrypted copy we cannot read." about a roster with thirty real names
 * on it. The claim would be false, which is the one failure this whole feature cannot survive.
 *
 * So conversion needs a pseudonym ASSIGNED, the real name sealed, and the class-document row
 * REWRITTEN rather than blanked. Blanking is what the other planner's clear step does, and a roster of
 * empty names is its own bug: lock the class and the grid shows thirty nameless rows.
 *
 * ── BUILT ON THE IMPORT SPLITTER, NOT BESIDE IT ─────────────────────────────────────────────────
 *
 * `planVaultImport` already does the hard half: order-preserving pseudonyms that never collide with a
 * real name on the roster, and the field-by-field decision about what is identifying. Reimplementing
 * either here would give this codebase two answers to the same question, which is how the banner
 * sentence and the restore path each ended up with a second copy that drifted. The only things this
 * adds are the two it cannot know about:
 *
 *   1. The student's EXISTING id, which must survive. Every grade key is `${studentId}-${assessmentId}`,
 *      and attendance, submissions, XP and seats are all keyed the same way. A conversion that issued
 *      new ids would be a data loss dressed as a privacy feature.
 *   2. The owner-only private roster, where `governmentId`, `parentName`, `parentEmail` and
 *      `parentPhone` live on an ordinary class. They are not on the class document, so the import
 *      splitter never sees them — and a conversion that left them behind would leave a readable copy
 *      of exactly the family contact details this mode promises not to hold.
 *
 * Pure, so the teacher can be shown what will happen before anything is written. This deletes the only
 * readable copy of a real name, and a plan a human can read is the difference between a conversion and
 * a leap.
 */

/** What this planner needs from a student on an ordinary, live class. */
export interface LiveStudentInput {
  id: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  email?: string;
  studentNumber?: string;
  pronouns?: string;
  /** Set by the nickname join. Their `firstName` IS their login, so it must never be reissued. */
  isNicknameOnly?: boolean;
  /** Course codes, notes, photo URLs — anything else rides along to the class document unchanged. */
  [key: string]: unknown;
}

/** A student number the app generated for a nickname join. Never the child's own identity. */
const STAMPED_STUDENT_NUMBER = /^NICK-/;

const nonEmpty = (v: unknown): string | undefined => {
  const s = typeof v === 'string' ? v.trim() : '';
  return s ? s : undefined;
};

/**
 * Is this student's class-document name ALREADY safe to leave on a classmate-readable array?
 *
 * The same question `planStudentRestore` asks about a deletion record, and it has to be asked here too
 * or the planner renames people who already have placeholders. Two cases:
 *
 *   A legacy NICKNAME class being upgraded. Its class document already holds a placeholder and the real
 *   name sits in the owner-only roster as `realFirstName`/`realLastName` — which is
 *   `migrateLegacyPii`'s job, not this one. Issuing a fresh placeholder over the existing one would
 *   rename every student in the class for no reason.
 *
 *   A student who joined by CODE and chose their own nickname. `isNicknameOnly` marks them, and
 *   nickname login resolves them by `firstName` — so reissuing it logs them out permanently. That is
 *   the bug round seven shipped on the restore path; it must not be reintroduced here.
 *
 * In both cases every OTHER identifying field still moves. Only the name stays.
 */
export function namesAlreadySafe(
  student: LiveStudentInput,
  priv: Record<string, unknown> | undefined,
  /** What the UNLOCKED vault holds for this student, when the caller can read it. */
  vaultRecord?: { firstName?: string; lastName?: string } | null,
): boolean {
  const row = student as Record<string, unknown>;

  /**
   * ── STAMPS FIRST, AND STAMPS ARE FACTS ──────────────────────────────────────────────────────
   *
   * Everything below this point is INFERENCE over the row's content — which pool a word is in,
   * whether the vault disagrees, whether a legacy field survives — and inference is what produced a
   * HIGH finding in four consecutive review rounds. Each round tuned it for the population the
   * previous round broke, and each tuning broke a population nobody had listed.
   *
   * A stamp is not inference. `isNicknameOnly` is written by the nickname join, `placeholderName` by
   * `planVaultImport`, by this planner and by the legacy migration's clear step, and `pseudonymised`
   * by `planSanitise`.
   *
   * `pseudonymised` was the population round twelve missed. `planSanitise` is irreversible, never
   * writes the vault and never writes `realFirstName` — so a sanitised class has NO vault record by
   * construction, and `planSanitiseClassWrite` has sealed such classes `privacyFirst` since
   * 2026-10-06. Those rows drew from the old word pool, so with the retired pool demoted to
   * corroboration they had no signal at all: the banner called every placeholder a readable real
   * name, Convert renamed the whole roster, and the restore sealed the old placeholder as the
   * child's identity.
   */
  if (row.isNicknameOnly === true) return true;
  if (row.placeholderName === true) return true;
  if (row.pseudonymised === true) return true;

  /**
   * BOTH LOCATIONS. A hydrated roster puts `realFirstName` in the owner-only private roster, but the
   * legacy form embedded it on the class-document row itself, and `STUDENT_PII_FIELDS` still lists it
   * so the sweep can strip it. A class in that older shape must not be renamed either.
   */
  if (nonEmpty(priv?.realFirstName) || nonEmpty(priv?.realLastName)
    || nonEmpty(row.realFirstName) || nonEmpty(row.realLastName)) return true;

  /**
   * ── THE REMAINING CASE: ROWS WRITTEN BEFORE THE STAMP EXISTED ───────────────────────────────
   *
   * A wizard-created class imported before `placeholderName` was added carries a placeholder on the
   * document and the real name in the vault, with no marker at all. The evidence is that BOTH names
   * are words from our own pools: every placeholder this app issues comes from `planVaultImport`,
   * which draws from them, and every personal name was deliberately removed from them — so a real
   * roster essentially never looks like this while a placeholder we issued always does. One word is
   * not evidence; the pools hold ordinary English.
   */
  const first = nonEmpty(student.firstName);
  const last = nonEmpty(student.lastName);
  if (!first || !last) return false;

  /**
   * ── TWO POOLS, TWO STRENGTHS ────────────────────────────────────────────────────────────────
   *
   * `RETIRED_WORDS` is the pool that was thrown out BECAUSE it was full of personal names — Bailey,
   * Jackson, Madison, Jordan, Hazel, Palmer, Nelson, Thatcher. Treating it as evidence on its own
   * meant a child called Jordan Palmer satisfied "both names are words from our own pools": the
   * conversion left their real name on the classmate-readable document, stamped the row
   * `placeholderName: true` so nothing would ever flag it again, and the banner went green.
   *
   * So the retired pool only CORROBORATES. On its own, the test is the current pool — objects,
   * materials and measurements, with every personal name deliberately removed, which no real roster
   * supplies two of.
   */
  const everOurWords = isPseudonymWord(first) && isPseudonymWord(last);
  if (!everOurWords) return false;

  const vf = nonEmpty(vaultRecord?.firstName);
  if (!vf) {
    /**
     * Nothing to corroborate with. Only the pool we still issue from counts — a retired-word pair is
     * as likely to be a real name as a placeholder, and guessing wrong leaves a child's name readable
     * under a banner claiming the class is protected.
     *
     * This costs nothing on the paths that matter: a migrated nickname class and a pre-stamp wizard
     * import both have a vault record whose name DIFFERS from the row, and every caller that decides
     * whether to convert or restore has the vault open.
     */
    return isIssuedWord(first) && isIssuedWord(last);
  }

  /**
   * The vault corroborates by DISAGREEING. The same name in both places means the document still
   * holds the real one — the state a seal that succeeded and a rewrite that failed leaves behind.
   */
  const same = (a: string | undefined, b: string | undefined) =>
    (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
  return !(same(vf, first) && same(nonEmpty(vaultRecord?.lastName), last));
}

/** The private-roster fields that are identifying and therefore move. */
export const LIVE_ROSTER_FIELDS = [
  'governmentId', 'parentName', 'parentEmail', 'parentPhone',
] as const;

export interface LiveConversionRow {
  /** UNCHANGED, so every grade, mark, absence and submission stays attached. */
  studentId: string;
  /** The pseudonym and the non-identifying remainder, to replace this student's class-document row. */
  publicPart: Record<string, unknown>;
  /** What gets encrypted. */
  vaultPart: VaultRecordPlain;
  /** Private-roster fields to delete once the ciphertext has been verified. */
  clearOnRoster: readonly string[];
  /** The pseudonym issued, so the caller can record it in `usedNicknames`. */
  pseudonym: Pseudonym;
}

export interface LiveConversionPlan {
  rows: LiveConversionRow[];
  /**
   * Students no pseudonym could be found for. NOTHING is converted for them — not even the fields that
   * would have moved cleanly — because a half-converted student is a real name on a roster the banner
   * has started calling protected.
   */
  exhausted: string[];
  /** Students with no identifying plaintext anywhere. Nothing to do, and not a problem. */
  alreadyClean: string[];
  /**
   * Students whose class-document NAME is being replaced — the ones with a real name readable by their
   * classmates right now.
   *
   * Separate from `rows` because the two answer different questions, and the banner needs this one.
   * `rows` is non-empty for a legacy NICKNAME class too (its email and student number still move), and
   * if the banner keyed its "convert" prompt on that it would pre-empt `migrateLegacyPii` — which is
   * the only thing that moves `realFirstName` out of the owner-only roster. A teacher would convert,
   * see green, and still have every real name stored in readable form one document away.
   *
   * So: a real name on the class document means CONVERT. Anything else means the migration.
   */
  renames: string[];
  /**
   * Students whose stored record could not be decrypted. Set aside rather than converted: the runner
   * refuses the class for them, and describing them as readable real names is simply false.
   */
  unreadable: string[];
  /**
   * Students whose name has no Latin first letter, so no placeholder pool exists for them at all.
   *
   * A SEPARATE list from `exhausted`, because the remedy is different and the wrong one is useless.
   * A name written in Arabic, Chinese or Punjabi script — ordinary on an Ontario roster — produced
   * `letterOf() === ''` and came back as "no placeholder names left", with copy blaming an unusually
   * large class. Deleting students would not help, the class could never be fully protected, and the
   * banner stayed amber forever with no action that would clear it.
   */
  noLatinLetter: string[];
}

/**
 * Work out what converting this class would do, without doing any of it.
 *
 * `alreadyIssued` carries pseudonyms the class has handed out before — relevant when a conversion is
 * retried after a partial run, or when some students joined under a nickname already.
 */
export function planLiveConversion(
  students: readonly LiveStudentInput[],
  studentPrivate: Record<string, Record<string, unknown>> | undefined,
  alreadyIssued: readonly Pseudonym[] = [],
  /**
   * What the unlocked vault holds, by studentId. The caller passes it because only it has the
   * session; omitting it makes the planner fall back to the weaker signals above.
   */
  vaultRecords: Record<string, { firstName?: string; lastName?: string } | null> = {},
  /**
   * Students whose stored record could not be DECRYPTED, as distinct from students who have none.
   *
   * `vaultRecords[id]` is `null` for both, and the two mean opposite things. A record that will not
   * open is almost certainly a placeholder with a real name behind it that this session's key cannot
   * read — so calling the row a readable real name is wrong, and the runner is going to refuse the
   * whole class anyway (`VaultRecordsUnreadableError`). Reported separately so the copy can say what
   * is actually wrong instead of "N students' real names are readable".
   */
  unreadable: readonly string[] = [],
): LiveConversionPlan {
  const plan: LiveConversionPlan = {
    rows: [], exhausted: [], alreadyClean: [], renames: [], noLatinLetter: [], unreadable: [],
  };
  const cannotOpen = new Set(unreadable);
  if (!students.length) return plan;

  /**
   * Merge each student's private-roster fields onto their row FIRST, so the import splitter makes one
   * decision over the whole of what is known about the child. `governmentId` and the guardian fields
   * are already in `VAULTED_IMPORT_FIELDS`, so they route themselves — no second list to keep in sync.
   */
  const merged: ImportRow[] = students.map((s) => {
    const priv = studentPrivate?.[s.id] ?? {};
    const row: ImportRow = { ...s } as ImportRow;
    for (const f of LIVE_ROSTER_FIELDS) {
      const v = nonEmpty(priv[f]);
      if (v) (row as Record<string, unknown>)[f] = v;
    }
    return row;
  });

  /**
   * Students with nothing identifying are set aside BEFORE pseudonyms are assigned, rather than
   * assigned one and then skipped. A word spent on a student who is not converted is a word another
   * student in the same letter cannot have, and the pools are the scarce resource here.
   */
  const convertible: { student: LiveStudentInput; row: ImportRow; index: number }[] = [];
  /** Students whose name stays: a legacy nickname class, or a self-chosen nickname. */
  const keepsName: { student: LiveStudentInput; row: ImportRow }[] = [];

  merged.forEach((row, i) => {
    const student = students[i];
    const priv = studentPrivate?.[student.id];
    const safeName = namesAlreadySafe(student, priv, vaultRecords[student.id]);

    /**
     * "Nothing to do" is judged on the fields that would actually MOVE. For a student whose name
     * stays, the name does not count — otherwise a nickname-only student with no email and no guardian
     * details would be queued for a conversion that changes nothing, and the banner would never go
     * green.
     */
    const movable = (VAULTED_IMPORT_FIELDS as readonly string[])
      .filter(f => !(safeName && (f === 'firstName' || f === 'lastName')));
    const hasIdentity = movable.some((f) => {
      const v = nonEmpty((row as Record<string, unknown>)[f]);
      if (!v) return false;
      /**
       * A `NICK-…` number does not count as something to move, or a code-joined student with nothing
       * else on their row is queued for a conversion that would move nothing — and the banner never
       * goes green on a class of them. It must not be BLANKED either: it is the stamp the roster's own
       * uniqueness check relies on.
       */
      if (f === 'studentNumber' && STAMPED_STUDENT_NUMBER.test(v)) return false;
      return true;
    });

    /**
     * A record that will not open is set aside BEFORE any of this. Converting it would seal over a
     * record we cannot read, and the runner refuses the class for it in any case — but the plan is
     * what the teacher reads, and "this student's details cannot be opened" is a different sentence
     * from "this student's real name is readable".
     */
    if (cannotOpen.has(student.id)) { plan.unreadable.push(student.id); return; }
    if (!hasIdentity) { plan.alreadyClean.push(student.id); return; }
    if (safeName) keepsName.push({ student, row });
    else convertible.push({ student, row, index: i });
  });

  /** Everything identifying except the name, lifted out and blanked. No placeholder is spent. */
  for (const { student, row } of keepsName) {
    const vaultPart: Record<string, unknown> = {};
    const publicPart: Record<string, unknown> = { id: student.id, placeholderName: true };
    for (const f of VAULTED_IMPORT_FIELDS) {
      if (f === 'firstName' || f === 'lastName') continue;
      const v = nonEmpty((row as Record<string, unknown>)[f]);
      /**
       * A `NICK-…` number is a stamp the nickname join writes for the roster's own uniqueness, not
       * the child's identity. Sealing it merges a machine stamp over the real number a teacher had
       * typed into the vault, and the student's own page then displays "NICK-1696…" as their student
       * number. The restore path fixes exactly this; the two must not disagree.
       */
      if (f === 'studentNumber' && v && STAMPED_STUDENT_NUMBER.test(v)) continue;
      if (v) vaultPart[f] = v;
      publicPart[f] = '';
    }
    const priv = studentPrivate?.[student.id] ?? {};
    plan.rows.push({
      studentId: student.id,
      publicPart,
      vaultPart: vaultPart as VaultRecordPlain,
      clearOnRoster: LIVE_ROSTER_FIELDS.filter(f => nonEmpty(priv[f])),
      // Their own name, restated so the caller can record it as issued without special-casing.
      pseudonym: {
        firstName: String(student.firstName ?? ''), lastName: String(student.lastName ?? ''),
      },
    });
  }

  if (!convertible.length) return plan;

  const split = planVaultImport(convertible.map(c => c.row), alreadyIssued);

  /**
   * `planVaultImport` reports exhaustion by INDEX INTO WHAT IT WAS GIVEN, which is the convertible
   * subset — not into `students`. Mapping it back through `convertible` is load-bearing: indexing
   * `students` directly would mark the wrong child as unconvertible as soon as one student was set
   * aside as already clean, and that student's real name would be the one left on the roster.
   */
  const exhaustedSet = new Set(split.exhausted);
  for (const i of split.exhausted) {
    const c = convertible[i];
    if (!c) continue;
    /**
     * Which KIND of refusal this is. `assignPseudonyms` reports both as exhaustion, but a name with
     * no Latin first letter has no pool to run out of — so "the class is large for one letter" is
     * wrong, and "remove an unused student" would never help.
     */
    /**
     * THE SAME FALLBACK `assignPseudonyms` USES, which is the last name OR the first.
     *
     * Testing both independently reported "no Latin letter" whenever EITHER name was empty — so a
     * student recorded with only "Smith", exhausted by a crowded S pool, was told their name does not
     * start with a letter from A to Z and to add a romanised spelling. It does, and that advice would
     * do nothing.
     */
    const forLetter = nonEmpty(c.student.lastName) ?? nonEmpty(c.student.firstName) ?? '';
    const noLetter = !letterOf(forLetter);
    (noLetter ? plan.noLatinLetter : plan.exhausted).push(c.student.id);
  }

  /**
   * The surviving rows come back in the splitter's own order, with the exhausted ones dropped. Walking
   * the convertible list in step with them requires skipping the same indices, in the same order.
   */
  let out = 0;
  convertible.forEach((c, i) => {
    if (exhaustedSet.has(i)) return;
    const row = split.rows[out];
    out += 1;
    if (!row) { plan.exhausted.push(c.student.id); return; }

    const priv = studentPrivate?.[c.student.id] ?? {};
    const clearOnRoster = LIVE_ROSTER_FIELDS.filter(f => nonEmpty(priv[f]));

    /**
     * EVERY VAULTED FIELD IS EXPLICITLY BLANKED, not merely left out.
     *
     * `planVaultImport` omits them, which is correct for a fresh import: the field was never on the
     * document, so there is nothing to remove. Conversion is the opposite case — the value is already
     * there. The class-document write merges the new values onto the existing row, so an OMITTED field
     * keeps whatever was in it: a converted class kept `email`, `studentNumber`, `preferredName` and
     * `pronouns` sitting readable on the classmate-visible array while the banner called it protected.
     *
     * `splitStudentPii` does not save us — `STUDENT_PII_FIELDS` deliberately excludes these four,
     * because on an ordinary class they legitimately belong on the class document. That is the same
     * conflation that broke the restore path in round six, arriving from the other direction.
     *
     * Blanked to '' rather than deleted, for the reason the import gives for `studentNumber`: an absent
     * field reads as "never had one", and a later writer would happily write a value back.
     */
    /**
     * ONLY THE FIELDS THAT CHANGE, not the whole row.
     *
     * `rewriteStudentFieldsOnClassDoc` merges what it is given onto the LIVE row, so anything else
     * included here overwrites whatever the server or another device has written since the panel was
     * opened — `xp`, `badges`, `dailyGames`, `studentHubAverage`, `gcUserId`, kinder tags. A student
     * finishing a daily game while the teacher reads the confirmation dialog had their XP and streak
     * silently reverted. The same class of bug is recorded elsewhere in this codebase as "taking
     * attendance erased every tag in the class".
     *
     * `planVaultImport` passes non-identifying extras through because it is building a NEW row. Here
     * they are already on the row, and the merge keeps them.
     */
    const publicPart: Record<string, unknown> = {
      id: c.student.id,
      firstName: row.publicPart.firstName,
      lastName: row.publicPart.lastName,
      // Stamped, so a re-run recognises its own placeholder instead of renaming again.
      placeholderName: true,
    };
    for (const f of VAULTED_IMPORT_FIELDS) {
      if (f === 'firstName' || f === 'lastName') continue;   // these carry the placeholder
      publicPart[f] = '';
    }

    plan.rows.push({
      studentId: c.student.id,
      // The id is restated because the row is what replaces the student on the class document.
      publicPart,
      vaultPart: row.vaultPart,
      clearOnRoster,
      pseudonym: {
        firstName: String(row.publicPart.firstName ?? ''),
        lastName: String(row.publicPart.lastName ?? ''),
      },
    });
    plan.renames.push(c.student.id);
  });

  return plan;
}

/**
 * What the teacher reads before pressing the button. Counts and field names, never a value.
 *
 * Deliberately names the irreversible part. The previous round of this work shipped a warning whose
 * advice steered an admin into an irreversible parent-facing release, so the rule here is that the
 * sentence describing a one-way step says so in the same breath as the count.
 */
export function describeLiveConversion(plan: LiveConversionPlan): string {
  /**
   * COUNTED FROM `renames`, not from `rows`.
   *
   * Every nickname student is a row — their email and student number still move — so a class of three
   * where two keep their nickname was told "3 students will be given a placeholder name". Two of them
   * already had one. A confirmation screen that overstates what it is about to do is how a teacher
   * learns not to read them.
   */
  const n = plan.renames.length;
  const kept = plan.rows.length - n;
  if (!n && !kept) {
    const blocked = [...describeExhaustion(plan)];
    if (blocked.length) return `Nothing can be converted. ${blocked.join(' ')}`;
    return 'Nothing to convert — this class holds no readable student details.';
  }

  const vaulted = new Set<string>();
  for (const r of plan.rows) for (const k of Object.keys(r.vaultPart)) vaulted.add(k);

  const parts: string[] = [];
  if (n) {
    parts.push(`${n} student${n === 1 ? '' : 's'} will be given a placeholder name, and their real `
      + `${[...vaulted].sort().join(', ')} will be encrypted on this device.`);
  }
  if (kept) {
    parts.push(`${kept} student${kept === 1 ? '' : 's'} keep${kept === 1 ? 's' : ''} the nickname `
      + `${kept === 1 ? 'they' : 'they'} already use; only their other details move.`);
  }
  parts.push('The readable copy on our servers is then deleted. That part cannot be undone: '
    + 'afterwards your passphrase or your recovery code is the only way to read these names.');
  /**
   * SAID, not left out. Replies to posts carry no author id, so there is nothing to match a student
   * to and they cannot be rewritten. The sanitise flow has always admitted this; this one claimed
   * "the readable copy is deleted" without the exception, which is the kind of omission that makes
   * the whole claim worth less than a smaller true one.
   */
  parts.push('One exception: replies that students have already written to each other keep the name '
    + 'they were written under, because a reply does not record who wrote it.');
  if (plan.alreadyClean.length) {
    parts.push(`${plan.alreadyClean.length} student${plan.alreadyClean.length === 1 ? ' has' : 's have'} `
      + 'nothing readable to move.');
  }
  parts.push(...describeExhaustion(plan));
  return parts.join(' ');
}

/** The two different reasons a student cannot be given a placeholder, each with its own remedy. */
function describeExhaustion(plan: LiveConversionPlan): string[] {
  const out: string[] = [];
  if (plan.exhausted.length) {
    const k = plan.exhausted.length;
    out.push(`${k} student${k === 1 ? '' : 's'} cannot be given a placeholder because this class has `
      + 'more students sharing one letter of the alphabet than there are words for it. '
      + `${k === 1 ? 'They will' : 'They will'} be left untouched.`);
  }
  if (plan.unreadable.length) {
    const k = plan.unreadable.length;
    out.push(`${k} student${k === 1 ? '\u2019s' : 's\u2019'} stored details cannot be opened with `
      + 'the key this browser is using. Lock the class and unlock it again with your passphrase, then '
      + 'come back — nothing will be converted until they can be read.');
  }
  if (plan.noLatinLetter.length) {
    const k = plan.noLatinLetter.length;
    out.push(`${k} student${k === 1 ? '\u2019s' : 's\u2019'} name does not start with a letter from `
      + 'A to Z, so there is no placeholder we can give them. Add a romanised spelling to their name '
      + `and ${k === 1 ? 'they' : 'they'} can be converted too.`);
  }
  if (out.length) {
    out.push('Until then this class will not be fully protected.');
  }
  return out;
}

/**
 * Placeholders this class has already handed out, so a plan cannot reissue one.
 *
 * Both sources, because neither is complete alone: `usedNicknames` holds words freed students no
 * longer occupy, and the roster holds words issued before `usedNicknames` was written.
 *
 * SHARED, because the banner and the panel each had their own version and they disagreed — the banner
 * passed nothing, so it could report no blocked students while the panel reported several exhausted,
 * and its row said "convert them to finish" about students the button could not finish.
 */
export const issuedPseudonymsOf = (
  classData: {
    students?: readonly { firstName?: string; lastName?: string }[];
    usedNicknames?: readonly string[];
  },
): Pseudonym[] => [
  ...(classData.usedNicknames ?? []).map(n => ({ firstName: n, lastName: n })),
  ...(classData.students ?? []).map(s => ({
    firstName: s.firstName ?? '', lastName: s.lastName ?? '',
  })),
];
